import {
  faCheck,
  faCloud,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components';

import type { AppointmentPatient } from '@/react-query/useAppointments';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import { buttonStyling } from '../styles';

import { TooltipButton } from './TooltipButton';
import { faCloudCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';

interface PMSNoteActionsProps {
  patient: AppointmentPatient | undefined;
  selectedNote: ClinicalNote;
  handleSaveNoteToPMS: () => void;
  isSavingNoteToPMS: boolean;
  handleRetryWritebackToPMS: () => void;
  isRetryingWriteback: boolean;
}

const getWritebackStatus = (
  selectedNote: ClinicalNote,
  patient: AppointmentPatient | undefined,
) => {
  const status = selectedNote?.writebackStatus;

  if (!patient?.pmsPatientId) return 'no-patient';
  if (!status?.clinicalNoteId) return 'can-save';
  if (status?.clinicalNoteId && status?.errorCode !== null) return 'failed';
  if (status?.clinicalNoteId && !status.processedAt) return 'processing';
  if (status?.isProcessed && status?.processedAt && !status.errorCode)
    return 'saved';
};

export const PMSNoteActions = ({
  patient,
  selectedNote,
  handleSaveNoteToPMS,
  isSavingNoteToPMS,
  handleRetryWritebackToPMS,
  isRetryingWriteback,
}: PMSNoteActionsProps) => {
  switch (getWritebackStatus(selectedNote, patient)) {
    case 'no-patient':
      return (
        <TooltipButton
          icon={faCloud}
          tooltip="Cannot sync this note. No matching patient found in your PMS."
          className={`${buttonStyling} vai-bg-gray-400 hover:vai-bg-gray-400 text-white`}
          disabled
        >
          Save to PMS
        </TooltipButton>
      );
    case 'can-save':
      return (
        <Button
          className={`${buttonStyling} hover:vai-ring-primary/50`}
          onClick={handleSaveNoteToPMS}
          disabled={isSavingNoteToPMS}
        >
          <span>
            <FontAwesomeIcon icon={faCloud} />
          </span>
          {isSavingNoteToPMS ? 'Saving...' : 'Save to PMS'}
        </Button>
      );
    case 'processing':
      return (
        <Button
          className={`${buttonStyling}`}
          disabled
        >
          <span>
            <FontAwesomeIcon icon={faSpinner} className="vai-animate-spin" />
          </span>
          Saving to PMS...
        </Button>
      );
    case 'failed':
      return (
        <Button
          className={`${buttonStyling} !vai-bg-red-500 hover:!vai-bg-red-600 !vai-text-white`}
          onClick={handleRetryWritebackToPMS}
          disabled={isRetryingWriteback}
        >
          <span>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </span>
          {isRetryingWriteback ? 'Retrying... ' : 'Sync failed, retry'}
        </Button>
      );
    case 'saved':
      return (
        <Button
          className={`${buttonStyling} !vai-bg-green-700 hover:!vai-bg-green-700/90 !vai-text-white`}
          disabled
        >
          <span>
            <FontAwesomeIcon icon={faCloudCheck} />
          </span>
          Saved to PMS
        </Button>
      );
  }
};
